import type L from "leaflet";
import type { LatLng } from "leaflet";
import type { PropertySelectionRow } from "../../types";
import { SAME_POSITION_MARGIN } from "./propertyRowPosition";
import { getPinProperty, isRowOfPinProperty } from "./pinProperty";

/** 地点とポップアップを持つピン（L.Marker・CanvasPinMarker） */
type PopupPin = L.Layer & { getLatLng: () => LatLng };

/** その地点に立ち、クリックを受け取るピンか */
const isInteractivePinAt = (layer: L.Layer, position: LatLng): boolean => {
  const { getLatLng } = layer as { getLatLng?: () => LatLng };
  return (
    typeof getLatLng === "function" &&
    (layer.options as L.InteractiveLayerOptions).interactive !== false &&
    getLatLng.call(layer).equals(position, SAME_POSITION_MARGIN)
  );
};

const isPopupPinAt = (layer: L.Layer, position: LatLng): layer is PopupPin =>
  // bind していないピンは undefined、unbindPopup した後は null になる
  layer.getPopup() != null && isInteractivePinAt(layer, position);

/**
 * 地図に載っているピンのうち、その地点に立ち、ポップアップを持たずにクリックで開くもの。無ければ null。
 * 解析済み登記情報のピン（クリックで詳細ダイアログを開く）が対象。
 * 物件一覧の青ピンを押した時に下の元のピンを押したことにするため、押した青ピン（exclude）は除く
 */
export const findClickPinAtPosition = (
  map: L.Map,
  position: LatLng,
  exclude: L.Layer
): L.Layer | null => {
  let found: L.Layer | null = null;
  map.eachLayer((layer) => {
    if (
      !found &&
      layer !== exclude &&
      layer.getPopup() == null &&
      layer.listens("click") &&
      isInteractivePinAt(layer, position)
    ) {
      found = layer;
    }
  });
  return found;
};

/**
 * 地図に載っているピンのうち、その地点に立ち、ポップアップを持つもの。無ければ null。
 * 検索結果・保存した物件リスト・受付帳・地図クリックのピンが対象。
 * 物件一覧の青ピンはポップアップを持たないので対象にしない。
 *
 * row を渡すと、その行の物件を表すピン（getPinProperty）を選ぶ。同じ地点に別の物件のピンしか
 * 無ければ null（別の物件のポップアップを開かないため）。物件を持たないピン（受付帳・地図クリック）は
 * 行と突き合わせられないので、地点が同じなら返す
 */
export const findPopupPinAtPosition = (
  map: L.Map,
  position: LatLng,
  row?: PropertySelectionRow
): L.Layer | null => {
  const pins: L.Layer[] = [];
  map.eachLayer((layer) => {
    if (isPopupPinAt(layer, position)) pins.push(layer);
  });
  if (!row) return pins[0] ?? null;

  const ofRow = pins.find((pin) => {
    const property = getPinProperty(pin);
    return property !== undefined && isRowOfPinProperty(row, property);
  });
  return ofRow ?? pins.find((pin) => getPinProperty(pin) === undefined) ?? null;
};
